const SITE = "https://onebigbit.com";

const softwareSchema = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: "Custom Software Development Company in Lucknow: How to Choose the Right Partner",
  description:
    "A practical guide to choosing a custom software development company in Lucknow, covering scope, technology, cost, and what to expect from a local IT partner.",
  image: `${SITE}/img/services-banner.jpg`,
  author: { "@type": "Organization", name: "OneBigBit Technologies Private Limited" },
  publisher: {
    "@type": "Organization",
    name: "OneBigBit Technologies Private Limited",
    logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
  },
  datePublished: "2026-10-08",
  dateModified: "2026-10-08",
  mainEntityOfPage: `${SITE}/blogs/custom-software-development-company-lucknow`,
};

const webAppSchema = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: "Website and Mobile App Development Services for Growing Businesses",
  description:
    "How website development and mobile app development work together, what a business should prepare, and how OneBigBit delivers both from Lucknow.",
  image: `${SITE}/img/webd.jpg`,
  author: { "@type": "Organization", name: "OneBigBit Technologies Private Limited" },
  publisher: {
    "@type": "Organization",
    name: "OneBigBit Technologies Private Limited",
    logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
  },
  datePublished: "2026-10-08",
  dateModified: "2026-10-08",
  mainEntityOfPage: `${SITE}/blogs/website-and-mobile-app-development-services`,
};

const marketingSchema = {
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: "Digital Marketing Services in Lucknow: What to Fix Before You Spend on Ads",
  description:
    "How businesses in Lucknow should prepare a website before digital marketing, from search basics and landing pages to what a first campaign should measure.",
  image: `${SITE}/img/digital-marketting.png`,
  author: { "@type": "Organization", name: "OneBigBit Technologies Private Limited" },
  publisher: {
    "@type": "Organization",
    name: "OneBigBit Technologies Private Limited",
    logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
  },
  datePublished: "2026-10-08",
  dateModified: "2026-10-08",
  mainEntityOfPage: `${SITE}/blogs/digital-marketing-services-lucknow`,
};

const staticBlogs = [
  {
    title: "Custom Software Development Company in Lucknow: How to Choose the Right Partner",
    slug: "custom-software-development-company-lucknow",
    url_text: "custom-software-development-company-lucknow",
    image_url: "/img/services-banner.jpg",
    short_des:
      "What to look for in a custom software development company in Lucknow, from discovery and technology choices to delivery, support, and cost.",
    meta_title: "Custom Software Development Company in Lucknow",
    meta_description:
      "Learn how to choose a custom software development company in Lucknow. Compare scope, technology, timelines, and support before you hire an IT partner.",
    keywords: [
      "custom software development company in Lucknow",
      "software development company Lucknow",
      "IT company in Lucknow",
      "custom software solutions",
    ],
    canonical_url: `${SITE}/blogs/custom-software-development-company-lucknow`,
    og_title: "Custom Software Development Company in Lucknow",
    og_description:
      "A practical guide to hiring a software development company in Lucknow for custom business software.",
    og_image: `${SITE}/img/services-banner.jpg`,
    og_type: "article",
    og_url: `${SITE}/blogs/custom-software-development-company-lucknow`,
    schema_markup: softwareSchema,
    createdAt: "2026-10-08T00:00:00.000Z",
    tags: ["software development", "Lucknow", "IT services"],
    share_links: {},
    toc: [
      { text: "Why custom software", anchor: "why-custom-software" },
      { text: "What a Lucknow IT partner should cover", anchor: "what-a-partner-should-cover" },
      { text: "Questions to ask before you hire", anchor: "questions-to-ask" },
      { text: "How OneBigBit delivers", anchor: "how-onebigbit-delivers" },
    ],
    faq: [
      {
        question: "What does a custom software development company in Lucknow build?",
        answer:
          "Typical work includes business applications, customer portals, management systems, integrations, and internal tools shaped around how a company already operates.",
      },
      {
        question: "How long does a custom software project take?",
        answer:
          "A focused first release often takes a few months. Larger platforms are delivered in phases so the business can start using the software before every feature is finished.",
      },
      {
        question: "Is a local IT company in Lucknow enough for clients outside the city?",
        answer:
          "Yes. Discovery can happen in person in Lucknow, Dehradun, or remotely. Delivery, demos, and support do not depend on the client sitting in the same office.",
      },
    ],
    long_des: `
      <p>Businesses searching for a custom software development company in Lucknow are usually trying to replace spreadsheets, disconnected tools, or a product that almost fits. The right IT partner starts from the workflow, not from a template.</p>
      <h2>Why custom software</h2>
      <p>Off-the-shelf software is a good fit when the process is standard. It becomes expensive when staff work around the product every day. Custom software follows the way orders, patients, students, field teams, or inventory actually move.</p>
      <p>OneBigBit Technologies, an IT company in Lucknow, builds that kind of software for education, healthcare, business operations, and conservation programmes. The goal is a system people will use, with reporting the management team can trust.</p>
      <h2>What a Lucknow IT partner should cover</h2>
      <p>A serious software development company in Lucknow should be able to take a project from discovery through launch, not only write screens. Ask for a clear path that includes:</p>
      <ul>
        <li>A written scope, with what is in the first release and what waits</li>
        <li>A technology choice explained in plain language</li>
        <li>Designs you can click through before development starts</li>
        <li>Regular demos, not a single handover at the end</li>
        <li>Training, documentation, and a support window after go-live</li>
      </ul>
      <p>Our <a href="/service/softwareDevelopment">custom software development</a> work follows that sequence so stakeholders can see progress instead of waiting for a surprise.</p>
      <h2>Questions to ask before you hire</h2>
      <p>Use the first meeting to test how the team thinks, not only how polished the proposal looks.</p>
      <ul>
        <li>Who will you talk to after the contract is signed?</li>
        <li>How are changes to scope estimated?</li>
        <li>Where is the source code stored, and who owns it?</li>
        <li>How is data backed up, and who can access production?</li>
        <li>What happens in the first month after launch?</li>
      </ul>
      <p>Clear answers matter more than a long feature list. A partner that cannot explain ownership, access, and support will be difficult to work with later.</p>
      <h2>How OneBigBit delivers</h2>
      <p>OneBigBit Technologies Private Limited is based in Aliganj, Lucknow, with teams that also support clients from Dehradun and Dubai. Projects range from management systems to customer portals and specialised platforms.</p>
      <p>If you are comparing IT companies in Lucknow, start with a short brief: who will use the software, what they do today, and what must be true on day one. Share that with us through the <a href="/contact">contact page</a> and we will tell you whether a custom build is the right next step, and what the first release should contain.</p>
    `,
  },
  {
    title: "Website and Mobile App Development Services for Growing Businesses",
    slug: "website-and-mobile-app-development-services",
    url_text: "website-and-mobile-app-development-services",
    image_url: "/img/webd.jpg",
    short_des:
      "When a business needs a new website, a mobile app, or both, and how to plan the first release so the site and the app stay easy to maintain.",
    meta_title: "Website and Mobile App Development Services",
    meta_description:
      "Plan website development and mobile app development together. See what to prepare, how the first release should be scoped, and how OneBigBit builds both.",
    keywords: [
      "website development company",
      "mobile app development company in Lucknow",
      "website and app development",
      "business website development",
    ],
    canonical_url: `${SITE}/blogs/website-and-mobile-app-development-services`,
    og_title: "Website and Mobile App Development Services",
    og_description:
      "How growing businesses should plan a website and a mobile app without building two disconnected products.",
    og_image: `${SITE}/img/webd.jpg`,
    og_type: "article",
    og_url: `${SITE}/blogs/website-and-mobile-app-development-services`,
    schema_markup: webAppSchema,
    createdAt: "2026-10-08T00:00:00.000Z",
    tags: ["website development", "mobile apps", "Lucknow"],
    share_links: {},
    toc: [
      { text: "Website, app, or both", anchor: "website-app-or-both" },
      { text: "What to prepare before development", anchor: "what-to-prepare" },
      { text: "A first release that can grow", anchor: "a-first-release" },
      { text: "How OneBigBit can help", anchor: "how-onebigbit-can-help" },
    ],
    faq: [
      {
        question: "Do I need a mobile app if I already have a website?",
        answer:
          "Not always. A fast, clear website is enough for many businesses. An app is worth it when customers or staff repeat the same tasks on a phone, such as bookings, orders, or field updates.",
      },
      {
        question: "Should the website and the app be built by the same team?",
        answer:
          "Yes, when they share customers, content, or data. One team can keep the design, accounts, and reporting consistent instead of stitching two products together later.",
      },
      {
        question: "Can OneBigBit build only the website first?",
        answer:
          "Yes. Many projects launch the website first, then add a mobile app once the content, offers, and customer journey are stable.",
      },
    ],
    long_des: `
      <p>A growing business usually needs two things online: a website people can find, and a simpler way for customers or staff to act on a phone. Website development and mobile app development solve different jobs. Planning them together keeps the brand, the content, and the data in one place.</p>
      <h2>Website, app, or both</h2>
      <p>Start with the website if people still need to understand who you are, what you offer, and how to reach you. Search, maps, and shared links all land on a website. A mobile app development company in Lucknow should not talk you into an app before that foundation exists.</p>
      <p>Add an app when the same person comes back often: booking a visit, checking an order, updating a field record, or completing a short task away from a desk. The app should do those repeat jobs well. It does not need to copy every page of the website.</p>
      <h2>What to prepare before development</h2>
      <p>Projects move faster when the business arrives with a few decisions already made:</p>
      <ul>
        <li>The primary visitor: a customer, a partner, or an employee</li>
        <li>The one action that matters most, such as a call, a form, or a booking</li>
        <li>Pages or screens that must exist on day one</li>
        <li>Content you already have: services, locations, photos, and proof of work</li>
        <li>Any system the new site or app must talk to, such as payments or an existing database</li>
      </ul>
      <p>Our <a href="/service/websiteDevelopment">website development</a> and <a href="/service/appDevelopment">mobile app development</a> teams use that list to separate the first release from later improvements.</p>
      <h2>A first release that can grow</h2>
      <p>The first public version should be small enough to launch and solid enough to trust. For a website, that usually means a clear home page, service pages, and a way to enquire. For an app, it means the core task, a login if one is required, and a simple way to get help.</p>
      <p>Design, content, and tracking belong in that first release. A page nobody can find, or an app nobody can measure, does not help the business. After launch, look at enquiries, drop-offs, and support questions, then improve the next version from that evidence.</p>
      <h2>How OneBigBit can help</h2>
      <p>OneBigBit Technologies Private Limited builds websites, web applications, and mobile apps for businesses that want a local team and a product they can keep improving. We are an IT company in Lucknow, and we also support clients beyond the city.</p>
      <p>If you are deciding between a new website, a mobile app, or a combined plan, send a short note through the <a href="/contact">contact page</a>. We will recommend the smaller starting point and what should wait until the first version is in use.</p>
    `,
  },
  {
    title: "Digital Marketing Services in Lucknow: What to Fix Before You Spend on Ads",
    slug: "digital-marketing-services-lucknow",
    url_text: "digital-marketing-services-lucknow",
    image_url: "/img/digital-marketting.png",
    short_des:
      "A practical order of work for digital marketing in Lucknow: make the website clear, then spend on search and ads you can measure.",
    meta_title: "Digital Marketing Services in Lucknow",
    meta_description:
      "Before you buy ads, fix the website. See how digital marketing services in Lucknow should cover search, landing pages, and measurable enquiries.",
    keywords: [
      "digital marketing services in Lucknow",
      "digital marketing company in Lucknow",
      "SEO company Lucknow",
      "IT company in Lucknow",
    ],
    canonical_url: `${SITE}/blogs/digital-marketing-services-lucknow`,
    og_title: "Digital Marketing Services in Lucknow",
    og_description:
      "What a Lucknow business should fix on its website before spending on digital marketing.",
    og_image: `${SITE}/img/digital-marketting.png`,
    og_type: "article",
    og_url: `${SITE}/blogs/digital-marketing-services-lucknow`,
    schema_markup: marketingSchema,
    createdAt: "2026-10-08T00:00:00.000Z",
    tags: ["digital marketing", "SEO", "Lucknow"],
    share_links: {},
    toc: [
      { text: "Start with the page people land on", anchor: "start-with-the-page" },
      { text: "Search before paid ads", anchor: "search-before-paid-ads" },
      { text: "What the first campaign should measure", anchor: "what-to-measure" },
      { text: "How OneBigBit approaches marketing", anchor: "how-onebigbit-approaches-marketing" },
    ],
    faq: [
      {
        question: "Do digital marketing services in Lucknow work for businesses outside the city?",
        answer:
          "Yes. Search and ads can target Lucknow, nearby cities, or a wider region. The website still needs a clear offer and a way for people to enquire.",
      },
      {
        question: "Should I run ads if my website is unfinished?",
        answer:
          "No. Ads send people to a page. If that page does not explain the service and offer a next step, the spend is wasted.",
      },
      {
        question: "How soon can a new site show up in search?",
        answer:
          "Useful pages can be indexed in days, but steady enquiries from search usually take longer. Paid ads can bring visits sooner while search builds.",
      },
    ],
    long_des: `
      <p>Digital marketing services in Lucknow are easy to buy and easy to waste. Ads, posts, and search only help when the page they open explains the offer and gives someone a way to respond. The website comes first.</p>
      <h2>Start with the page people land on</h2>
      <p>A visitor from search or an ad should see three things without hunting: what you do, who it is for, and how to talk to you. A home page that lists every service in the same size, with no phone number and no form, will not turn visits into enquiries.</p>
      <p>Before a campaign, check the pages you would be proud to send a customer. Service names should match the words people actually search. Locations, proof of work, and a working contact path belong on those pages. If the site is slow or hard to read on a phone, fix that before you pay for more visitors.</p>
      <h2>Search before paid ads</h2>
      <p>Search is how people find a software company, a clinic system, or a local service when they already have a need. A digital marketing company in Lucknow should be able to show which pages can rank, which phrases are realistic, and which pages are missing.</p>
      <p>Paid ads are useful when you need visits this month, or when you are testing an offer. They are a poor substitute for a page that answers the question. OneBigBit treats <a href="/service/digitalMarketing">digital marketing</a> and <a href="/service/websiteDevelopment">website development</a> as one job: the page and the campaign should agree.</p>
      <h2>What the first campaign should measure</h2>
      <p>Impressions are not the result. For most businesses the result is an enquiry: a call, a form, or a visit booked. The first campaign should answer a smaller set of questions:</p>
      <ul>
        <li>Which page did people open?</li>
        <li>Did they reach the contact form or the phone number?</li>
        <li>Which offer produced enquiries, and which only produced clicks?</li>
        <li>Are those enquiries from the customers you wanted?</li>
      </ul>
      <p>Review that after a few weeks, then change the page or the spend. A report that only shows clicks does not tell you whether the marketing paid for itself.</p>
      <h2>How OneBigBit approaches marketing</h2>
      <p>OneBigBit Technologies Private Limited is an IT company in Lucknow. We build the website or application and, when it is ready, help the business get found. That includes search, content that matches real services, and campaigns tied to a page that can convert.</p>
      <p>If you already have a site and want to know what to fix before you advertise, send the address through the <a href="/contact">contact page</a>. We will tell you whether the next step is the website, the marketing, or both.</p>
    `,
  },
];

export function getBlogBySlug(slug) {
  const key = decodeURIComponent(slug || "");
  return staticBlogs.find((blog) => blog.slug === key || blog.url_text === key) || null;
}

export default staticBlogs;
